import { clientResult } from "~/errors/clientResult";
import { parseVidsString, withTranslation } from "~/units/scripture";
import type { UseQueryReturnType } from "@tanstack/vue-query";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import type {
  QueryCommentaryResults,
  SolrClientError,
  SolrClientInterface,
} from "@rsc/vv-solr-client";
import type { CommentaryFilters } from "~/stores/useCommentaryFiltersStore";
import type { SerializableResult } from "~/errors/util";
import { makeResultAsyncSerializable } from "~/errors/util";
import type { ScriptureClientInterface } from "@rsc/scripture-client";
import { normalizeTemporarySolrGroups } from "./processCommentarySearchResults/temporarySolrCompatibility";
import { ResultAsync, errAsync, okAsync } from "neverthrow";
import { VoxVivaError } from "~/errors/classes";
import {
  getCommentaryTypeOrFallback,
  knownCommentaryTypes,
} from "~/static/taxonomy/commentaryTypes";
import {
  processCommentarySearchResultsGroup,
  reGroupCommentarySearchResults,
} from "~/composables/processCommentarySearchResults/processing";
import {
  getFlatResultsFromGroups,
  nestAndSortCommentary,
} from "~/composables/processCommentarySearchResults/grouping";
import type {
  OuterCommentaryGroup,
  ProcessedCommentaryComment,
  ProcessedCommentaryImage,
} from "~/composables/processCommentarySearchResults/types";
import { bucketsToMap } from "~/composables/processCommentarySearchResults/util";
import { detailLevels } from "~/static/taxonomy/detailLevels";
import { defaultLocale } from "~/i18n/locales";
import type { LogFunction } from "~/logging/log";

/**
 * Query the Solr search engine for commentary data, and process the results.
 */
export function useCommentaryQuery(
  solrClient: SolrClientInterface,
  logServerSideError: LogFunction,
  filters: Ref<CommentaryFilters>,
  languages: Ref<string[]>,
  scriptureClient?: ScriptureClientInterface,
): UseQueryReturnType<
  SerializableResult<ProcessedQueryCommentaryResults>,
  Error
> {
  return useQuery({
    queryKey: ["commentaryQuery", filters, languages],
    queryFn: () =>
      makeResultAsyncSerializable(
        () => queryCommentary(solrClient, filters, languages, scriptureClient),
        logServerSideError,
      ),
  });
}

/**
 * Don't initiate a commentary query, but reuse the one from the cache if it's available.
 */
export function useCachedCommentaryQuery(
  filters: Ref<CommentaryFilters>,
  languages: Ref<string[]>,
): SerializableResult<ProcessedQueryCommentaryResults> | undefined {
  return useQueryClient().getQueryData<
    SerializableResult<ProcessedQueryCommentaryResults>
  >(["commentaryQuery", filters, languages]);
}

/**
 * Helper function for `useCommentaryQuery`.
 *
 * This is only exported so that we can spy on it in unit tests. Don't use it directly.
 */
export function queryCommentary(
  solrClient: SolrClientInterface,
  filters: Ref<CommentaryFilters>,
  languages: Ref<string[]>,
  scriptureClient?: ScriptureClientInterface,
): ResultAsync<
  ProcessedQueryCommentaryResults,
  VoxVivaError | SolrClientError
> {
  const queryRange = parseVidsString(filters.value.vids, null)[0] || null;
  if (!queryRange) {
    return errAsync(new VoxVivaError("Invalid VID range."));
  }

  // When the detail level weight is -1, it means that we do NOT want to filter by detail level.
  const detailLevelWeightFilter =
    filters.value.detailLevelWeight === -1
      ? null
      : filters.value.detailLevelWeight;

  const commentaryType = getCommentaryTypeOrFallback(
    filters.value.commentaryTypeMachineName,
  );
  const typeMachineName = commentaryType.machineName;
  const sortGroupsNarrow = commentaryType.sortGroupsNarrow;

  const languagesParam = languages.value.length
    ? languages.value
    : [defaultLocale];

  const solrResult = solrClient.queryCommentary(
    withTranslation(queryRange, null),
    typeMachineName,
    detailLevelWeightFilter,
    true,
    languagesParam,
    0,
    200,
    0,
    50,
    [
      "index_id",
      "its_nid",
      "its_detail_weight",
      "sm_type_machine_name",
      "zm_scripture",
      "bs_status",
      "its_weight",
      "ss_heading",
      "ss_search_api_language",
      "zs_body_teaser",
      "sm_lw_workflow",
      "sm_flag_names",
      "ss_title",
      "tm_X3b_en_description",
      "zs_original_url",
      "zs_large_url",
      "zs_small_url",
      "zs_placeholder_base64",
    ],
  );

  return clientResult(solrResult)
    .andThen((solrData) =>
      // TEMPORARY SCRIP-40: remove after BOTH production indexes finish rebuilding.
      ResultAsync.fromPromise(
        normalizeTemporarySolrGroups(
          solrData.grouped.ss_computed_group.groups,
          scriptureClient,
        ),
        (error) =>
          new VoxVivaError(
            `Legacy Solr compatibility failed: ${String(error)}`,
          ),
      ).map((groups) => ({
        ...solrData,
        grouped: {
          ...solrData.grouped,
          ss_computed_group: { ...solrData.grouped.ss_computed_group, groups },
        },
      })),
    )
    .andThen((solrData) => {
      const outerGroups = reGroupCommentarySearchResults(
        solrData.grouped.ss_computed_group.groups,
        queryRange,
      )
        .map((result) =>
          result.map((group) =>
            processCommentarySearchResultsGroup(group.doclist, group.group),
          ),
        )
        .map((result) =>
          nestAndSortCommentary(result, sortGroupsNarrow, languages.value),
        );

      if (outerGroups.isErr()) {
        return errAsync(outerGroups.error);
      }

      const flatResults = getFlatResultsFromGroups(outerGroups.value);

      const languagesBuckets = solrData.facets?.languages?.buckets || [];

      return okAsync({
        outerGroups: outerGroups.value,
        flatResults,
        numResults: solrData.facets?.count || 0,
        commentaryTypeCounts: getCommentaryTypesCounts(
          solrData.facets,
          languagesParam,
        ),
        detailLevelCounts: getCumulativeDetailLevelCounts(
          solrData.facets,
          languagesParam,
        ),
        languageCounts: bucketsToMap(languagesBuckets),
      });
    });
}

export type CommentarySearchResults = QueryCommentaryResults<
  [
    // Common fields.
    "index_id",
    "its_nid",
    "its_detail_weight",
    "sm_type_machine_name",
    "zm_scripture",
    "bs_status",
    "its_weight",
    "ss_search_api_language",

    // Comment fields.
    "ss_heading",
    "zs_body_teaser",
    "sm_lw_workflow",
    "sm_flag_names",

    // Image fields.
    "ss_title",
    "tm_X3b_en_description",
    "zs_original_url",
    "zs_large_url",
    "zs_small_url",
    "zs_placeholder_base64",
  ]
>;

export function getCommentaryTypesCounts(
  searchResultsFacets: CommentarySearchResults["facets"] | null,
  languagesParam: string[],
): Map<string, Map<string, number>> {
  const languagesBuckets = searchResultsFacets?.languages?.buckets || [];
  const emptyCommentaryTypesMap = new Map(
    knownCommentaryTypes.map((type) => [type.machineName, 0]),
  );
  return languagesBuckets.reduce(
    (acc, current) => {
      const { val } = current;
      acc.set(
        val,
        bucketsToMap(
          current?.commentaryTypes?.buckets || [],
          new Map(emptyCommentaryTypesMap), // let us have all key with count 0
        ),
      );
      return acc;
    },
    new Map(
      languagesParam.map((languageCode) => [
        languageCode,
        emptyCommentaryTypesMap, // default Map let us have all key with count 0 for empty language result
      ]),
    ),
  );
}

export function getCumulativeDetailLevelCounts(
  searchResultsFacets: CommentarySearchResults["facets"] | null,
  languagesParam: string[],
): Map<string, Map<number, number>> {
  const languagesBuckets = searchResultsFacets?.languages?.buckets || [];
  const keys = detailLevels.map((level) => level.weight || 0);
  const emptyDetailLevelMap = new Map(
    detailLevels.map((level) => [level.weight, 0]),
  );

  return languagesBuckets.reduce(
    (acc, current) => {
      const { val, detailLevels } = current;
      const counts = bucketsToMap<number>(detailLevels?.buckets || []);
      const cumulativeCountsMap = (() => {
        const countMap = new Map();
        let acc = 0;
        keys.forEach((key: number) => {
          acc += counts.get(key) || 0;
          countMap.set(key, acc);
        });
        return countMap;
      })();
      acc.set(val, cumulativeCountsMap);
      return acc;
    },
    new Map(
      languagesParam.map((languageCode) => [languageCode, emptyDetailLevelMap]), // default Map let us have all key with count 0 for empty language result
    ),
  );
}

export type ProcessedQueryCommentaryResults = {
  /**
   * The processed (nested, grouped, and sorted) commentary search result groups.
   */
  outerGroups: OuterCommentaryGroup[];

  /**
   * All results, flattened, in order from top to bottom.
   */
  flatResults: (ProcessedCommentaryComment | ProcessedCommentaryImage)[];

  /**
   * The number of results.
   */
  numResults: number;

  /**
   * The number of available comments per commentary type per language.
   *
   * This is a nested map of commentary type counts
   * The outer map is keyed by language.
   * The inner maps are keyed by the machine names of the commentary types.
   * The values are the number of available comments for each type.
   */
  commentaryTypeCounts: Map<string, Map<string, number>>;

  /**
   * The number of available comments per detail level per language.
   *
   * This is a nested map of detail level counts.
   *  The outer map is keyed by language.
   * The inner maps are keyed by level weights.
   * The values are the number of available comments for each level plus all levels below it.
   */
  detailLevelCounts: Map<string, Map<number, number>>;

  /**
   * The number of available comments per language.
   */
  languageCounts: Map<string, number>;
};
